import SEOHead from "../components/SEOHead";
import B2BContactForm from "../components/B2BContactForm";
import { COMPANY } from "../data/siteData";
import { imageUrl } from "../lib/images";

export default function KontaktPage() {
  return (
    <div className="bg-[#070d18] text-slate-100 min-h-screen">
      <SEOHead
        title="Kontakt"
        description="Kontakt KVC Express ApS for at drøfte faste B2B-transportaftaler inden for køle-, medicinal- og fødevaretransport."
      />

      {/* Hero Header */}
      <section className="relative pt-16 pb-20 lg:pt-24 lg:pb-28 border-b border-slate-800 bg-[#081224] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#0F766E]/20 border border-[#2DD4BF]/40 text-[#2DD4BF] text-xs font-mono uppercase tracking-widest">
              <i className="fa-solid fa-envelope-open-text text-[10px]" aria-hidden="true" />
              <span>Direkte B2B Kontakt</span>
            </div>

            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight font-display">
              Lad os tale om et fast samarbejde
            </h1>

            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              Vi indgår i faste aftaler med virksomheder, der har et løbende og professionelt behov for køle- og medicinaltransport. Kontakt os i dag for en uforpligtende samtale om jeres ruter og krav.
            </p>
          </div>
        </div>
      </section>

      {/* Main Grid: Info + Form */}
      <section className="py-20 bg-[#070d18]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Col: Company Details & B2B Qualification (5 cols) */}
            <div className="lg:col-span-5 space-y-8">
              <div className="p-8 rounded-2xl bg-[#0b1426] border border-slate-800 space-y-6 shadow-xl">
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-[#2DD4BF] mb-2">
                    Virksomhedskontakt
                  </div>
                  <h2 className="text-2xl font-bold text-white font-display">
                    {COMPANY.name}
                  </h2>
                  <div className="text-xs font-mono text-slate-400 mt-1">
                    CVR: <span className="text-slate-200 font-semibold">{COMPANY.cvr}</span>
                  </div>
                </div>

                <div className="space-y-4 pt-2 border-t border-slate-800 text-sm">
                  <div className="flex items-start gap-3.5">
                    <div className="w-9 h-9 rounded-lg bg-[#0F766E]/20 border border-[#2DD4BF]/30 flex items-center justify-center text-[#2DD4BF] flex-shrink-0">
                      <i className="fa-solid fa-location-dot text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <div className="text-xs font-mono text-slate-400 uppercase">Lokation & Dækning</div>
                      <div className="text-white font-medium mt-0.5">{COMPANY.location}</div>
                      <div className="text-[#2DD4BF] text-xs font-medium">{COMPANY.coverage}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5">
                    <div className="w-9 h-9 rounded-lg bg-[#0F766E]/20 border border-[#2DD4BF]/30 flex items-center justify-center text-[#2DD4BF] flex-shrink-0">
                      <i className="fa-solid fa-phone text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <div className="text-xs font-mono text-slate-400 uppercase">Telefon</div>
                      <a
                        href={`tel:${COMPANY.phoneRaw}`}
                        className="text-white font-medium mt-0.5 hover:text-[#2DD4BF] transition block whitespace-nowrap"
                      >
                        <span className="whitespace-nowrap">{COMPANY.phone}</span>
                      </a>
                      <div className="text-slate-400 text-xs mt-0.5">Mandag–fredag kl. 10.00–17.00</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5">
                    <div className="w-9 h-9 rounded-lg bg-[#0F766E]/20 border border-[#2DD4BF]/30 flex items-center justify-center text-[#2DD4BF] flex-shrink-0">
                      <i className="fa-solid fa-envelope text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <div className="text-xs font-mono text-slate-400 uppercase">E-mail</div>
                      <a
                        href={`mailto:${COMPANY.email}`}
                        className="text-white font-medium mt-0.5 hover:text-[#2DD4BF] transition block whitespace-nowrap"
                      >
                        <span className="whitespace-nowrap">{COMPANY.email}</span>
                      </a>
                      <div className="text-slate-400 text-xs mt-0.5">Vi svarer normalt inden for én hverdag.</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* B2B Guidance Box */}
              <div className="p-6 rounded-2xl bg-[#081020] border border-[#0F766E]/40 text-xs text-slate-300 space-y-3">
                <div className="font-bold text-white text-sm flex items-center gap-2">
                  <i className="fa-solid fa-circle-info text-[#2DD4BF]" aria-hidden="true" />
                  <span>Transportaftaler & Dækning</span>
                </div>
                <p className="leading-relaxed text-sm text-slate-200">
                  Vi indgår transportaftaler med virksomheder og leverer til både erhvervs- og privatadresser.
                </p>
                <div className="text-[11px] font-mono text-[#2DD4BF] pt-1">
                  Dækker hele Danmark inkl. Bornholm
                </div>
              </div>

              {/* Service & Levering Billede */}
              <div className="rounded-2xl overflow-hidden border border-slate-800 bg-[#081224] shadow-xl">
                <img
                  src={imageUrl("/_assets/shutterstock-1770826502.jpg", "width=600,quality=80,format=auto")}
                  alt="KVC Express chauffør leverer forsendelse med personlig service"
                  className="w-full h-48 sm:h-56 object-cover"
                />
                <div className="p-4 text-xs text-slate-400 bg-[#0b1426] border-t border-slate-800 flex items-center gap-2">
                  <i className="fa-solid fa-handshake text-[#2DD4BF]" aria-hidden="true" />
                  <span>Fast chaufførkorps med fokus på punktlighed og god kundedialog</span>
                </div>
              </div>
            </div>

            {/* Right Col: Contact Form (7 cols) */}
            <div className="lg:col-span-7">
              <B2BContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
